import { cheapQuality, hammingDistance } from './extractPhotoMetadata';
import type { StagedPhotoMeta } from './bulkImportClustering';

export interface BurstGroup {
  representative: StagedPhotoMeta;
  collapsed: StagedPhotoMeta[];
}

export interface FaceQualitySignal {
  clientId: string;
  faceCount: number;
  /** Largest detected face's bounding-box area relative to the photo, 0-1. */
  maxFaceAreaRatio: number;
  /** Average eyes-open score across detected faces, 0-1 (1 = clearly open). */
  eyesOpenScore: number;
}

export interface CurationResult {
  groups: BurstGroup[];
  faceCandidates: StagedPhotoMeta[];
  featured: StagedPhotoMeta[];
  more: StagedPhotoMeta[];
}

export interface BurstOptions {
  burstWindowMs?: number;
  hashDistance?: number;
  maxFaceCandidates?: number;
  defaultFeaturedCount?: number;
}

// Tunable defaults — see the project plan's "calibrate against a real photo
// library" step before shipping these to all users.
export const BURST_WINDOW_MS = 2 * 60 * 1000;
export const BURST_HASH_DISTANCE = 5;
export const MAX_FACE_CANDIDATES = 8;
export const DEFAULT_FEATURED_COUNT = 5;

function byCheapQualityDesc(a: StagedPhotoMeta, b: StagedPhotoMeta): number {
  return cheapQuality(b.sharpness, b.exposure) - cheapQuality(a.sharpness, a.exposure);
}

/**
 * Tier 2 — collapses consecutive (already time-sorted) photos into bursts
 * when they're close in time AND visually near-identical (dHash Hamming
 * distance), keeping the highest-cheapQuality shot as each burst's
 * representative. Nothing is discarded — `collapsed` photos are still fully
 * available, just hidden by default.
 */
export function collapseBursts(photos: StagedPhotoMeta[], opts: BurstOptions = {}): BurstGroup[] {
  const windowMs = opts.burstWindowMs ?? BURST_WINDOW_MS;
  const hashDistance = opts.hashDistance ?? BURST_HASH_DISTANCE;

  const groups: BurstGroup[] = [];
  let current: StagedPhotoMeta[] = [];

  for (const photo of photos) {
    if (current.length === 0) {
      current.push(photo);
      continue;
    }
    const prev = current[current.length - 1];
    const withinTime = Boolean(prev.takenAt && photo.takenAt) &&
      Math.abs(photo.takenAt!.getTime() - prev.takenAt!.getTime()) <= windowMs;
    const withinHash = hammingDistance(photo.hash, prev.hash) <= hashDistance;

    if (withinTime && withinHash) {
      current.push(photo);
    } else {
      groups.push(toBurstGroup(current));
      current = [photo];
    }
  }
  if (current.length > 0) groups.push(toBurstGroup(current));
  return groups;
}

function toBurstGroup(photos: StagedPhotoMeta[]): BurstGroup {
  const sorted = [...photos].sort(byCheapQualityDesc);
  const [representative, ...collapsed] = sorted;
  return { representative, collapsed };
}

/**
 * Tier 3 — from each cluster's post-burst representatives, take the top
 * `maxFaceCandidates` by cheapQuality. This bounds face-detection call volume
 * to a fixed number per cluster regardless of how many raw photos were
 * selected — the structural fix for keeping face detection from feeling slow.
 */
export function selectFaceCandidates(groups: BurstGroup[], opts: BurstOptions = {}): StagedPhotoMeta[] {
  const max = opts.maxFaceCandidates ?? MAX_FACE_CANDIDATES;
  return groups
    .map(g => g.representative)
    .sort(byCheapQualityDesc)
    .slice(0, max);
}

/**
 * Tier 4 — final ranking of the face-detection shortlist, combining
 * cheapQuality with a face-quality bonus (face count, prominence, eyes-open)
 * when signals are available. Without signals yet (detection still running),
 * ranks on cheapQuality alone — used for the initial progressive reveal
 * before face detection finishes, then re-ranked once signals arrive.
 */
export function rankFeaturedPhotos(
  candidates: StagedPhotoMeta[],
  faceSignals: FaceQualitySignal[] = [],
  opts: BurstOptions = {}
): { featured: StagedPhotoMeta[]; rest: StagedPhotoMeta[] } {
  const count = opts.defaultFeaturedCount ?? DEFAULT_FEATURED_COUNT;
  const signalByClientId = new Map(faceSignals.map(s => [s.clientId, s]));

  const scored = candidates.map(photo => {
    const signal = signalByClientId.get(photo.clientId);
    const faceBonus = signal
      ? 0.3 * Math.min(1, signal.faceCount / 3) + 0.4 * signal.maxFaceAreaRatio + 0.3 * signal.eyesOpenScore
      : 0;
    return { photo, score: cheapQuality(photo.sharpness, photo.exposure) + faceBonus };
  });
  scored.sort((a, b) => b.score - a.score);

  const featured = scored.slice(0, count).map(s => s.photo);
  const featuredIds = new Set(featured.map(p => p.clientId));
  const rest = candidates.filter(p => !featuredIds.has(p.clientId));
  return { featured, rest };
}

/**
 * Runs all four tiers for one cluster. `more` is every photo in the cluster
 * that isn't featured — burst-collapsed duplicates, face-shortlist stragglers,
 * and low-quality rejects alike — still fully uploadable, just unchecked by
 * default in the review UI.
 */
export function curateCluster(
  photos: StagedPhotoMeta[],
  faceSignals: FaceQualitySignal[] = [],
  opts: BurstOptions = {}
): CurationResult {
  const groups = collapseBursts(photos, opts);
  const faceCandidates = selectFaceCandidates(groups, opts);
  const { featured } = rankFeaturedPhotos(faceCandidates, faceSignals, opts);
  const featuredIds = new Set(featured.map(p => p.clientId));
  const more = photos.filter(p => !featuredIds.has(p.clientId));
  return { groups, faceCandidates, featured, more };
}
